import PropTypes from "prop-types";
import React from "react";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import { FieldGroup, MCard, MSpinner } from "components/Shared";
import { dateFormatter, dateTimeToString } from "../Shared/Formatters";
import AppInsightsTracker from "components/Shared/AppInsightsTracker";
import PayPeriodDropdown from "components/User/PayPeriodDropdown.tsx";

class TimeLogs extends AppInsightsTracker {
    constructor(props) {
        super(props);
        this.displayName = "TimeLogs";
        this.navigatePayPeriod = this.navigatePayPeriod.bind(this);
        this.getHours = this.getHours.bind(this);
        this.state = {
            caseCount: 0,
            totalHours: 0,
            selectedPayPeriodIndex: props.selectedPayPeriodIndex ? props.selectedPayPeriodIndex : 0
        };
    }

    componentDidMount() {
        if (this.props.payPeriods.length === 0) {
            this.props.getPayPeriodList();
        } else {
            const { getCaseTimeLogByUserPayPeriod, payPeriods } = this.props;
            getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, payPeriods[this.state.selectedPayPeriodIndex].id);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { isFetchingPayPeriods, getCaseTimeLogByUserPayPeriod, payPeriods } = this.props;
        // get the time logs by the first payperiod that comes back.
        if (!isFetchingPayPeriods && prevProps.isFetchingPayPeriods) {
            getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, payPeriods[prevState.selectedPayPeriodIndex].id);
        }
    }

    navigatePayPeriod(newSelectedIndex) {
        this.setState({ selectedPayPeriodIndex: newSelectedIndex });
        if (this.props.callBackPayPeriod) this.props.callBackPayPeriod(newSelectedIndex);
        // refresh the list
        if (newSelectedIndex == null) {
            //  pending list
            this.props.getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, this.props.payPeriods[0].id);
        }
        else {
            // actual pay period
            this.props.getCaseTimeLogByUserPayPeriod(this.props.internetUserId ? this.props.internetUserId : 0, this.props.payPeriods[newSelectedIndex].id);
        }
    }

    getHours() {
        const { timeLogs } = this.props;
        if (timeLogs.length > 0) {
            return (timeLogs.map(timeLog => {
                return timeLog.timeMinutes;
            }).reduce((prev, curr) => {
                return prev + curr;
            }) / 60).toFixed(2);
        }
        return (0).toFixed(2);
    }

    render() {
        const {
            isFetchingTimeLogs,
            timeLogs,
            payPeriods,
            isFetchingPayPeriods
        } = this.props;

        const { selectedPayPeriodIndex } = this.state;

        if (isFetchingTimeLogs || isFetchingPayPeriods) {
            return <MSpinner />;
        }

        let payrollStatus = payPeriods[this.state.selectedPayPeriodIndex].closedDate == null ? "Pending" : dateTimeToString(payPeriods[this.state.selectedPayPeriodIndex].closedDate);

        let payPeriodValue = payPeriods[this.state.selectedPayPeriodIndex].fromToDisplay;
        let closedOnValue = payrollStatus.toString();
        let casesValue = timeLogs.length.toString();
        let totalHours = this.getHours();

        let fieldLabels = ["Pay Period ", "Closed on ", "Cases ", "Production Hours "];
        let fieldValues = [payPeriodValue, closedOnValue, casesValue, totalHours];

        if (this.props.fieldSet === SchedulingAssistantFieldSet) {
            fieldLabels = ["Pay Period ", "Closed on "];
            fieldValues = [payPeriodValue, closedOnValue];
        }

        const timeLogSummaryCard = () => {
            return (
                <MCard title="Time Log Summary">
                    <div style={{ marginTop: '16px' }}>
                        <PayPeriodDropdown
                            payPeriods={payPeriods}
                            selectedPayPeriodIndex={selectedPayPeriodIndex}
                            payPeriodChangeCallback={this.navigatePayPeriod}
                        />
                    </div>
                    <Divider sx={{ my: 2 }} />
                    <FieldGroup
                        labels={fieldLabels}
                        values={fieldValues}
                    />
                </MCard>
            );
        };

        const summaryAndTable = () => {
            return (
                <Grid container spacing={2} className="case-header" >
                    <Grid item xs={4}>
                        {timeLogSummaryCard()}
                    </Grid>
                    <Grid item xs={12}>
                        <Card>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Control #</TableCell>
                                            <TableCell>Address</TableCell>
                                            <TableCell>Survey Type</TableCell>
                                            <TableCell>Work Type</TableCell>
                                            <TableCell>Minutes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {timeLogs.map((t, index) => (
                                            <TableRow key={"t" + index}>
                                                <TableCell>{dateFormatter(t.createDate, "ddd MMM D YYYY")}</TableCell>
                                                <TableCell>{t.controlIdNumber}</TableCell>
                                                <TableCell>{t.surveyAddress}</TableCell>
                                                <TableCell>{t.surveyTypeName}</TableCell>
                                                <TableCell>{t.workTypeName}</TableCell>
                                                <TableCell>{t.timeMinutes} Minutes</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Grid>
                </Grid>
            );
        };
        return (
            this.props.displayJustSummaryCard ? timeLogSummaryCard() : summaryAndTable()
        );
    }
}

export const SchedulingAssistantFieldSet = "SchedulingAssistant";

TimeLogs.propTypes = {
    isFetchingTimeLogs: PropTypes.bool.isRequired,
    timeLogs: PropTypes.array.isRequired,
    payPeriods: PropTypes.array.isRequired,
    isFetchingPayPeriods: PropTypes.bool.isRequired,
    getCaseTimeLogByUserPayPeriod: PropTypes.func.isRequired,
    getPayPeriodList: PropTypes.func.isRequired,
    displayJustSummaryCard: PropTypes.bool,
    callBackPayPeriod: PropTypes.func,
    selectedPayPeriodIndex: PropTypes.number,
    internetUserId: PropTypes.number,
    fieldSet: PropTypes.oneOf([SchedulingAssistantFieldSet]), // changes the default fields in the card. if you want to customize the fields for other displays, just add the string here in the array
};

TimeLogs.defaultProps = {
    displayJustSummaryCard: false,
};

export default TimeLogs;